import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GenerateLandingPageModal from './GenerateLandingPageModal.js';

const LandingPagesView = () => {
  const [landingPages, setLandingPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  useEffect(() => {
    fetchLandingPages();
  }, []);

  const getPublicUrl = (page) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5005';
    return `${baseUrl}/api/public/landing-pages/${page.company_id}/${page.slug}`;
  };

  const getPreviewUrl = (page) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5005';
    const previewToken = 'preview-' + Date.now();
    return `${baseUrl}/api/public/landing-pages/${page.company_id}/${page.slug}/preview?token=${previewToken}`;
  };

  const handleViewPage = (page) => {
    if (page.status === 'published') {
      window.open(getPublicUrl(page), '_blank');
    } else {
      window.open(getPreviewUrl(page), '_blank');
    }
  };

  const handlePublishPage = async (page) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/landing-pages/${page.id}`, { status: 'published' }, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      });
      
      // Refresh the list
      fetchLandingPages();
    } catch (error) {
      console.error('Error publishing page:', error);
      alert('Failed to publish page: ' + (error.response?.data?.message || error.message));
    }
  };

  const fetchLandingPages = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if we have auth token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('Fetching landing pages...');
      const response = await axios.get('/api/landing-pages', {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Landing pages response:', response.data);
      setLandingPages(response.data);
    } catch (error) {
      console.error('Error fetching landing pages:', error);
      setError(error.response?.data?.message || error.message || 'Failed to fetch landing pages');
      
      // Don't automatically logout - let the auth interceptor handle it
      if (error.response?.status === 404) {
        setError('Landing pages API endpoint not found. Make sure the server is running with the latest code.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2">Loading landing pages...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Landing Pages</h1>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading Landing Pages</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={fetchLandingPages}
                  className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Troubleshooting Steps:</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>1. Make sure your backend server is running</li>
            <li>2. Verify the database migration was run: <code className="bg-blue-100 px-1 rounded">node backend/scripts/run_landing_pages_migration.js</code></li>
            <li>3. Check that the API routes are properly mounted in server.js</li>
            <li>4. Verify you have the <code className="bg-blue-100 px-1 rounded">mailer</code> permission</li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Landing Pages</h1>
          <p className="text-gray-600">Create and manage AI-generated landing pages</p>
          <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            🧪 Experimental Feature - we're still working on this and would love your feedback :)
          </div>
        </div>
        <button
          onClick={() => setShowGenerateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
        >
          <span>Generate New Page</span>
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500">Total Pages</div>
          <div className="text-2xl font-bold text-gray-900">{landingPages.length}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500">Published</div>
          <div className="text-2xl font-bold text-green-600">
            {landingPages.filter(p => p.status === 'published').length}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500">Total Views</div>
          <div className="text-2xl font-bold text-blue-600">
            {landingPages.reduce((sum, page) => sum + (page.page_views || 0), 0)}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-sm font-medium text-gray-500">Total Leads</div>
          <div className="text-2xl font-bold text-purple-600">
            {landingPages.reduce((sum, page) => sum + (page.form_submissions || 0), 0)}
          </div>
        </div>
      </div>

      {/* Landing Pages List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Your Landing Pages</h3>
        </div>
        <div className="p-6">
          {landingPages.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 text-6xl mb-4">📄</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No landing pages yet</h3>
              <p className="text-gray-600">Create your first AI-generated landing page to get started!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {landingPages.map(page => (
                <div key={page.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-900">{page.name}</h4>
                      <p className="text-sm text-gray-600">{page.title}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        /{page.company_id}/{page.slug} • {page.status} • {page.page_views || 0} views
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleViewPage(page)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        View
                      </button>
                      {page.status !== 'published' && (
                        <button 
                          onClick={() => handlePublishPage(page)}
                          className="text-sm text-green-600 hover:text-green-800"
                        >
                          Publish
                        </button>
                      )}
                      <button className="text-sm text-gray-600 hover:text-gray-800">Edit</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Generate Modal */}
      {showGenerateModal && (
        <GenerateLandingPageModal
          onClose={() => setShowGenerateModal(false)}
          onSuccess={() => {
            setShowGenerateModal(false);
            fetchLandingPages();
          }}
        />
      )}
    </div>
  );
};

export default LandingPagesView;